// Collision — brush-based (Quake-style) + AABB backwards compatible
// Engine code: deterministic, fixed-point.
//
// A brush is a convex volume defined by planes. AABB is a special case (6 planes).
// ClipVelocity projects velocity onto contact surfaces — this IS surfing.

import { FP, type Fixed } from "./fixed.js";

// --- Types ---

export type Plane = {
  nx: Fixed; ny: Fixed; nz: Fixed; // normal (unit vector)
  d: Fixed;                         // distance from origin (point on plane: dot(n, p) = d)
};

export type AABB = {
  minX: Fixed; minY: Fixed; minZ: Fixed;
  maxX: Fixed; maxY: Fixed; maxZ: Fixed;
};

export type Brush = {
  planes: Plane[];
  bounds: AABB; // broad-phase bounding box
  material?: number; // physical surface material index (0 = interior, 1 = exterior)
  surfacePlaneCount?: number; // Facet mode (Q3 patch collision):
  // planes[0..surfacePlaneCount-1] are SURFACE planes (actual geometry).
  // planes[surfacePlaneCount..] are BORDER planes (containment only).
  // Only hits through surface planes are accepted; border hits are rejected.
  // When undefined, all planes are surface planes (regular brush behavior).
};

export type TraceResult = {
  fraction: Fixed;  // 0..1, how far along the move before hitting
  normal: { nx: Fixed; ny: Fixed; nz: Fixed }; // contact surface normal
  hit: boolean;
  material: number; // surface material index of the hit brush (0 = default)
};

export type SlideResult = {
  px: Fixed; py: Fixed; pz: Fixed;
  vx: Fixed; vy: Fixed; vz: Fixed;
  onGround: boolean;
};

// --- Helpers ---

function dot3(ax: Fixed, ay: Fixed, az: Fixed, bx: Fixed, by: Fixed, bz: Fixed): Fixed {
  return FP.add(FP.add(FP.mul(ax, bx), FP.mul(ay, by)), FP.mul(az, bz));
}

// Create AABB from center + dimensions
export function aabb(cx: number, cy: number, cz: number, w: number, h: number, d: number): AABB {
  return {
    minX: FP.from(cx - w / 2), minY: FP.from(cy - h / 2), minZ: FP.from(cz - d / 2),
    maxX: FP.from(cx + w / 2), maxY: FP.from(cy + h / 2), maxZ: FP.from(cz + d / 2),
  };
}

// Convert AABB to a 6-plane brush (backwards compatible)
export function aabbToBrush(a: AABB): Brush {
  return {
    planes: [
      { nx: FP.ONE,  ny: FP.ZERO, nz: FP.ZERO, d: a.maxX },   // +X
      { nx: -FP.ONE as Fixed, ny: FP.ZERO, nz: FP.ZERO, d: ((-a.minX) | 0) as Fixed }, // -X
      { nx: FP.ZERO, ny: FP.ONE,  nz: FP.ZERO, d: a.maxY },   // +Y
      { nx: FP.ZERO, ny: -FP.ONE as Fixed, nz: FP.ZERO, d: ((-a.minY) | 0) as Fixed }, // -Y
      { nx: FP.ZERO, ny: FP.ZERO, nz: FP.ONE,  d: a.maxZ },   // +Z
      { nx: FP.ZERO, ny: FP.ZERO, nz: -FP.ONE as Fixed, d: ((-a.minZ) | 0) as Fixed }, // -Z
    ],
    bounds: a,
  };
}

// Create a brush from planes, auto-compute bounding box
export function brush(planes: Plane[]): Brush {
  // Approximate bounds from plane distances
  let minX = FP.from(-1000), minY = FP.from(-1000), minZ = FP.from(-1000);
  let maxX = FP.from(1000), maxY = FP.from(1000), maxZ = FP.from(1000);
  for (const p of planes) {
    if (p.nx > FP.from(0.9)) maxX = FP.min(maxX, p.d);
    if (p.nx < FP.from(-0.9)) minX = FP.max(minX, ((-p.d) | 0) as Fixed);
    if (p.ny > FP.from(0.9)) maxY = FP.min(maxY, p.d);
    if (p.ny < FP.from(-0.9)) minY = FP.max(minY, ((-p.d) | 0) as Fixed);
    if (p.nz > FP.from(0.9)) maxZ = FP.min(maxZ, p.d);
    if (p.nz < FP.from(-0.9)) minZ = FP.max(minZ, ((-p.d) | 0) as Fixed);
  }
  return { planes, bounds: { minX, minY, minZ, maxX, maxY, maxZ } };
}

// --- AABB broad-phase ---

function aabbOverlap(
  px: Fixed, py: Fixed, pz: Fixed,
  hw: Fixed, hh: Fixed, hd: Fixed,
  b: AABB
): boolean {
  return FP.add(px, hw) > b.minX && FP.sub(px, hw) < b.maxX &&
         FP.add(py, hh) > b.minY && FP.sub(py, hh) < b.maxY &&
         FP.add(pz, hd) > b.minZ && FP.sub(pz, hd) < b.maxZ;
}

// --- ClipVelocity (Quake PM_ClipVelocity) ---

const OVERBOUNCE = FP.from(1.001); // slight overcorrection to prevent sticking

// Maximum FP truncation error for dot3. Each FP.mul loses up to 1 unit,
// dot3 sums 2-3 terms, and position drift from accepted remaining adds ~1.
// A player at dist in [-SURFACE_EPS, 0] is "on the surface", not "inside".
const SURFACE_EPS = 3; // raw FP units (0.003 real)

export function clipVelocity(
  vx: Fixed, vy: Fixed, vz: Fixed,
  nx: Fixed, ny: Fixed, nz: Fixed,
  overbounce: Fixed = OVERBOUNCE
): { vx: Fixed; vy: Fixed; vz: Fixed } {
  const backoff = FP.mul(dot3(vx, vy, vz, nx, ny, nz), overbounce);
  return {
    vx: FP.sub(vx, FP.mul(nx, backoff)),
    vy: FP.sub(vy, FP.mul(ny, backoff)),
    vz: FP.sub(vz, FP.mul(nz, backoff)),
  };
}

// --- Sweep point against brush ---
// Returns fraction (0..1) and contact normal.
// The point is expanded by player half-extents (Minkowski approach:
// expand brush planes by half-extents, trace a point).

export function sweepAgainstBrush(
  startX: Fixed, startY: Fixed, startZ: Fixed,
  endX: Fixed, endY: Fixed, endZ: Fixed,
  hw: Fixed, hh: Fixed, hd: Fixed,
  b: Brush
): TraceResult {
  let enterFrac = FP.from(-1) as Fixed; // -1 in fixed
  let leaveFrac = FP.ONE;
  let hitNx = FP.ZERO, hitNy = FP.ZERO, hitNz = FP.ZERO;
  let entryIsSurface = true; // Q3 facet: track if entry plane is a surface plane

  // surfacePlaneCount: when set, planes[0..spc-1] are surface planes,
  // planes[spc..] are border planes. Only surface plane entries are valid.
  const spc = b.surfacePlaneCount;

  const dx = FP.sub(endX, startX);
  const dy = FP.sub(endY, startY);
  const dz = FP.sub(endZ, startZ);

  for (let i = 0; i < b.planes.length; i++) {
    const p = b.planes[i];

    // Expand plane by player half-extents (Minkowski sum)
    const expand = FP.add(FP.add(
      FP.mul(FP.abs(p.nx), hw),
      FP.mul(FP.abs(p.ny), hh)),
      FP.mul(FP.abs(p.nz), hd)
    );
    const adjustedD = FP.add(p.d, expand);

    // Distance of start and end from expanded plane
    const startDist = FP.sub(dot3(startX, startY, startZ, p.nx, p.ny, p.nz), adjustedD);
    const endDist = FP.sub(dot3(endX, endY, endZ, p.nx, p.ny, p.nz), adjustedD);

    // Strictly outside at both endpoints → outside the brush entirely
    if (startDist > 0 && endDist > 0) {
      return { fraction: FP.ONE, normal: { nx: FP.ZERO, ny: FP.ZERO, nz: FP.ZERO }, hit: false };
    }

    // ON or near the surface (within FP truncation noise).
    if (startDist >= -SURFACE_EPS && startDist <= 0) {
      // For facets: only re-enter through surface planes, not borders
      if (spc !== undefined && i >= spc) continue;
      if (endDist >= 0) continue; // moving away or parallel
      if (FP.ZERO > enterFrac) {
        enterFrac = FP.ZERO;
        hitNx = p.nx; hitNy = p.ny; hitNz = p.nz;
        entryIsSurface = spc === undefined || i < spc;
      }
      continue;
    }

    if (startDist < -SURFACE_EPS && endDist <= 0) continue; // both behind

    // Crossing this plane — compute fraction
    const denom = FP.sub(startDist, endDist);
    if (denom === 0) continue;
    const frac = FP.div(startDist, denom);

    if (startDist > 0) {
      if (frac > enterFrac) {
        enterFrac = frac;
        hitNx = p.nx; hitNy = p.ny; hitNz = p.nz;
        entryIsSurface = spc === undefined || i < spc;
      }
    } else {
      if (frac < leaveFrac) {
        leaveFrac = frac;
      }
    }
  }

  if (enterFrac < leaveFrac && enterFrac >= 0) {
    // Q3 facet rejection: if we entered through a border plane, reject the hit.
    // The player entered from the side (segment boundary), not the front face.
    if (!entryIsSurface) {
      return { fraction: FP.ONE, normal: { nx: FP.ZERO, ny: FP.ZERO, nz: FP.ZERO }, hit: false };
    }
    const f = FP.clamp(enterFrac, FP.ZERO, FP.ONE);
    return {
      fraction: f,
      normal: { nx: hitNx, ny: hitNy, nz: hitNz },
      hit: true,
    };
  }

  // Check if start point is fully inside the brush (all planes behind).
  // For facets, only consider surface planes for push-out direction.
  let allBehind = true;
  let shallowest = FP.from(-999999) as Fixed;
  let pushNx = FP.ZERO, pushNy = FP.ONE, pushNz = FP.ZERO;

  for (let i = 0; i < b.planes.length; i++) {
    const p = b.planes[i];
    const expand = FP.add(FP.add(
      FP.mul(FP.abs(p.nx), hw),
      FP.mul(FP.abs(p.ny), hh)),
      FP.mul(FP.abs(p.nz), hd)
    );
    const dist = FP.sub(
      dot3(startX, startY, startZ, p.nx, p.ny, p.nz),
      FP.add(p.d, expand)
    );
    if (dist >= -SURFACE_EPS) { allBehind = false; break; }
    // For facets, only push out along surface planes
    const isSurf = spc === undefined || i < spc;
    if (isSurf && dist > shallowest) {
      shallowest = dist;
      pushNx = p.nx; pushNy = p.ny; pushNz = p.nz;
    }
  }

  if (allBehind) {
    return { fraction: shallowest, normal: { nx: pushNx, ny: pushNy, nz: pushNz }, hit: true };
  }

  return { fraction: FP.ONE, normal: { nx: FP.ZERO, ny: FP.ZERO, nz: FP.ZERO }, hit: false };
}

// --- Fixed-point cross product (used by slideMove crease handling) ---

function cross3(
  ax: Fixed, ay: Fixed, az: Fixed,
  bx: Fixed, by: Fixed, bz: Fixed,
): { x: Fixed; y: Fixed; z: Fixed } {
  return {
    x: FP.sub(FP.mul(ay, bz), FP.mul(az, by)),
    y: FP.sub(FP.mul(az, bx), FP.mul(ax, bz)),
    z: FP.sub(FP.mul(ax, by), FP.mul(ay, bx)),
  };
}

// --- SlideMove (Quake 3 PM_SlideMove with multi-plane crease handling) ---
// Tries to move, clips against brushes, slides along surfaces.
// Up to 4 iterations to handle corners.
//
// Key difference from naive clip: tracks ALL planes hit across iterations.
// When clipping against plane N pushes velocity back into a previously-hit
// plane, we project velocity along the crease (cross product of the two
// normals). This is what prevents catching on brush segment seams in surf.
// Directly based on Quake 3's PM_SlideMove in bg_slidemove.c.

const MAX_CLIP_PLANES = 5;

export function slideMove(
  px: Fixed, py: Fixed, pz: Fixed,
  vx: Fixed, vy: Fixed, vz: Fixed,
  hw: Fixed, hh: Fixed, hd: Fixed,
  brushes: Brush[],
  dt: Fixed
): SlideResult {
  let onGround = false;
  let remainX = FP.mul(vx, dt);
  let remainY = FP.mul(vy, dt);
  let remainZ = FP.mul(vz, dt);

  // Plane accumulator — Quake 3 stores all planes hit during the slide
  const planeNx: Fixed[] = [];
  const planeNy: Fixed[] = [];
  const planeNz: Fixed[] = [];
  let numPlanes = 0;

  // Track last frac=0 hit normal to detect FP noise loops
  let lastZeroNx = FP.ZERO, lastZeroNy = FP.ZERO, lastZeroNz = FP.ZERO;
  let hadZeroHit = false;

  for (let iter = 0; iter < 4; iter++) {
    if (FP.add(FP.add(FP.abs(remainX), FP.abs(remainY)), FP.abs(remainZ)) < FP.from(0.001)) break;

    const endX = FP.add(px, remainX);
    const endY = FP.add(py, remainY);
    const endZ = FP.add(pz, remainZ);

    // Find closest hit across all brushes
    let bestFrac = FP.ONE;
    let bestNx = FP.ZERO, bestNy = FP.ZERO, bestNz = FP.ZERO;
    let anyHit = false;

    for (let i = 0; i < brushes.length; i++) {
      const b = brushes[i];
      const midX = FP.div(FP.add(px, endX), FP.from(2));
      const midY = FP.div(FP.add(py, endY), FP.from(2));
      const midZ = FP.div(FP.add(pz, endZ), FP.from(2));
      const extX = FP.add(hw, FP.div(FP.abs(remainX), FP.from(2)));
      const extY = FP.add(hh, FP.div(FP.abs(remainY), FP.from(2)));
      const extZ = FP.add(hd, FP.div(FP.abs(remainZ), FP.from(2)));
      if (!aabbOverlap(midX, midY, midZ, extX, extY, extZ, b.bounds)) continue;

      const result = sweepAgainstBrush(px, py, pz, endX, endY, endZ, hw, hh, hd, b);
      if (result.hit && result.fraction < bestFrac) {
        bestFrac = result.fraction;
        bestNx = result.normal.nx;
        bestNy = result.normal.ny;
        bestNz = result.normal.nz;
        anyHit = true;
      }
    }

    if (!anyHit) {
      px = endX; py = endY; pz = endZ;
      break;
    }

    // Detect FP noise loop: same surface returning frac=0 twice in a row.
    if (bestFrac === 0 && hadZeroHit &&
        bestNx === lastZeroNx && bestNy === lastZeroNy && bestNz === lastZeroNz) {
      px = FP.add(px, remainX);
      py = FP.add(py, remainY);
      pz = FP.add(pz, remainZ);
      break;
    }

    if (bestFrac === 0) {
      lastZeroNx = bestNx; lastZeroNy = bestNy; lastZeroNz = bestNz;
      hadZeroHit = true;
    } else {
      hadZeroHit = false;
    }

    let moveFrac: Fixed;

    if (bestFrac < 0) {
      // Start inside brush — push out by penetration depth
      const pushAmount = FP.sub(FP.from(0.002), bestFrac) as Fixed;
      px = FP.add(px, FP.mul(bestNx, pushAmount));
      py = FP.add(py, FP.mul(bestNy, pushAmount));
      pz = FP.add(pz, FP.mul(bestNz, pushAmount));
      moveFrac = FP.ZERO;
    } else {
      moveFrac = bestFrac;
      px = FP.add(px, FP.mul(remainX, moveFrac));
      py = FP.add(py, FP.mul(remainY, moveFrac));
      pz = FP.add(pz, FP.mul(remainZ, moveFrac));
    }

    // Ground detection: hit a mostly-upward surface
    if (bestNy > FP.from(0.7)) {
      onGround = true;
    }

    // --- Quake 3 multi-plane crease handling ---
    // Check if this plane is a duplicate of one we already hit (skip if so)
    let isDuplicate = false;
    for (let k = 0; k < numPlanes; k++) {
      if (dot3(bestNx, bestNy, bestNz, planeNx[k], planeNy[k], planeNz[k]) > FP.from(0.99)) {
        // Nearly the same plane — nudge velocity away from it instead of
        // adding a duplicate. This handles re-hitting the same surface due
        // to FP imprecision.
        vx = FP.add(vx, bestNx);
        vy = FP.add(vy, bestNy);
        vz = FP.add(vz, bestNz);
        isDuplicate = true;
        break;
      }
    }
    if (isDuplicate) {
      // Recompute remaining from nudged velocity
      remainX = FP.mul(vx, FP.sub(dt, FP.mul(dt, moveFrac)));
      remainY = FP.mul(vy, FP.sub(dt, FP.mul(dt, moveFrac)));
      remainZ = FP.mul(vz, FP.sub(dt, FP.mul(dt, moveFrac)));
      continue;
    }

    // Add this plane to the accumulator
    if (numPlanes < MAX_CLIP_PLANES) {
      planeNx[numPlanes] = bestNx;
      planeNy[numPlanes] = bestNy;
      planeNz[numPlanes] = bestNz;
      numPlanes++;
    }

    // Try clipping velocity against ALL accumulated planes.
    // For each plane i, clip velocity. Then check if the result goes into
    // any other plane j. If it does, project along the crease of i and j.
    let solved = false;
    let cvx = vx, cvy = vy, cvz = vz;

    for (let i = 0; i < numPlanes; i++) {
      // Clip original velocity against plane i
      const clip1 = clipVelocity(vx, vy, vz, planeNx[i], planeNy[i], planeNz[i]);
      cvx = clip1.vx; cvy = clip1.vy; cvz = clip1.vz;

      // Check if this clipped velocity is valid against all other planes
      let valid = true;
      for (let j = 0; j < numPlanes; j++) {
        if (j === i) continue;
        if (dot3(cvx, cvy, cvz, planeNx[j], planeNy[j], planeNz[j]) >= 0) continue;

        // Clipped velocity goes into plane j. Clip against j too.
        const clip2 = clipVelocity(cvx, cvy, cvz, planeNx[j], planeNy[j], planeNz[j]);
        cvx = clip2.vx; cvy = clip2.vy; cvz = clip2.vz;

        // If double-clip still goes into plane i, use the crease direction
        if (dot3(cvx, cvy, cvz, planeNx[i], planeNy[i], planeNz[i]) >= 0) continue;

        // Project velocity along the crease of planes i and j
        const crease = cross3(planeNx[i], planeNy[i], planeNz[i],
                              planeNx[j], planeNy[j], planeNz[j]);
        const cLenSq = dot3(crease.x, crease.y, crease.z, crease.x, crease.y, crease.z);
        if (cLenSq < FP.from(0.001)) {
          // Planes are (anti)parallel — stuck, zero velocity
          cvx = FP.ZERO; cvy = FP.ZERO; cvz = FP.ZERO;
          valid = true;
          break;
        }

        // Project original velocity onto crease direction
        const d = dot3(crease.x, crease.y, crease.z, vx, vy, vz);
        cvx = FP.div(FP.mul(crease.x, d), cLenSq);
        cvy = FP.div(FP.mul(crease.y, d), cLenSq);
        cvz = FP.div(FP.mul(crease.z, d), cLenSq);

        // Check if crease-projected velocity goes into a third plane
        let blocked = false;
        for (let k = 0; k < numPlanes; k++) {
          if (k === i || k === j) continue;
          if (dot3(cvx, cvy, cvz, planeNx[k], planeNy[k], planeNz[k]) < 0) {
            // Three planes conflict — stuck in a corner, zero velocity
            cvx = FP.ZERO; cvy = FP.ZERO; cvz = FP.ZERO;
            blocked = true;
            break;
          }
        }
        if (blocked) { valid = true; break; }
        valid = true;
        break;
      }

      if (valid) { solved = true; break; }
    }

    if (solved) {
      vx = cvx; vy = cvy; vz = cvz;
    }

    // Compute remaining movement from clipped velocity
    const remainFrac = FP.sub(FP.ONE, moveFrac);
    remainX = FP.mul(vx, FP.mul(dt, remainFrac));
    remainY = FP.mul(vy, FP.mul(dt, remainFrac));
    remainZ = FP.mul(vz, FP.mul(dt, remainFrac));
  }

  return { px, py, pz, vx, vy, vz, onGround };
}

// --- StepMove (Source StepMove) ---
// When grounded and blocked by a wall, try stepping up and over.
// Tries both flat slide and step-up slide, picks whichever goes farther horizontally.

const STEP_SIZE = FP.from(0.5); // ~18 Source units scaled to our units

export function stepSlideMove(
  px: Fixed, py: Fixed, pz: Fixed,
  vx: Fixed, vy: Fixed, vz: Fixed,
  hw: Fixed, hh: Fixed, hd: Fixed,
  brushes: Brush[],
  dt: Fixed,
  wasOnGround: boolean,
): SlideResult {
  // Path A: flat slide (always)
  const flat = slideMove(px, py, pz, vx, vy, vz, hw, hh, hd, brushes, dt);

  // Only attempt step-up if was on ground (airborne players don't step)
  if (!wasOnGround) return flat;
  const flatDx = FP.sub(flat.px, px), flatDz = FP.sub(flat.pz, pz);
  const flatDistSq = FP.add(FP.mul(flatDx, flatDx), FP.mul(flatDz, flatDz));

  // Path B: step up, slide, step down
  // 1. Trace up by STEP_SIZE
  let stepPy = FP.add(py, STEP_SIZE);
  // Quick check: is the space above blocked? Sweep upward.
  const upCheck = sweepAllBrushes(px, py, pz, px, stepPy, pz, hw, hh, hd, brushes);
  if (upCheck.hit) {
    stepPy = FP.add(py, FP.mul(STEP_SIZE, upCheck.fraction));
  }

  // 2. Slide from elevated position
  const stepped = slideMove(px, stepPy, pz, vx, vy, vz, hw, hh, hd, brushes, dt);

  // 3. Trace down to settle back on ground
  const downTarget = FP.sub(stepped.py, STEP_SIZE);
  const downCheck = sweepAllBrushes(stepped.px, stepped.py, stepped.pz, stepped.px, downTarget, stepped.pz, hw, hh, hd, brushes);

  if (downCheck.hit) {
    // Check if landing surface is walkable (normal.y >= 0.7)
    if (downCheck.normal.ny < FP.from(0.7)) return flat; // too steep, reject step

    stepped.py = FP.add(stepped.py, FP.mul(FP.sub(downTarget, stepped.py), downCheck.fraction));
    stepped.onGround = true;
  } else {
    stepped.py = downTarget;
  }

  // Pick whichever went farther horizontally
  const stepDx = FP.sub(stepped.px, px), stepDz = FP.sub(stepped.pz, pz);
  const stepDistSq = FP.add(FP.mul(stepDx, stepDx), FP.mul(stepDz, stepDz));

  if (stepDistSq > flatDistSq) {
    // Step path won — use stepped position but flat Z velocity (Source behavior)
    stepped.vy = flat.vy;
    return stepped;
  }
  return flat;
}

/** Sweep against all brushes, return closest hit */
export function sweepAllBrushes(
  sx: Fixed, sy: Fixed, sz: Fixed,
  ex: Fixed, ey: Fixed, ez: Fixed,
  hw: Fixed, hh: Fixed, hd: Fixed,
  brushes: Brush[],
): TraceResult {
  let bestFrac = FP.ONE;
  let bestNx = FP.ZERO, bestNy = FP.ZERO, bestNz = FP.ZERO;
  let anyHit = false;
  let bestMat = 0;

  for (const b of brushes) {
    const result = sweepAgainstBrush(sx, sy, sz, ex, ey, ez, hw, hh, hd, b);
    if (result.hit && result.fraction < bestFrac && result.fraction >= 0) {
      bestFrac = result.fraction;
      bestNx = result.normal.nx; bestNy = result.normal.ny; bestNz = result.normal.nz;
      bestMat = b.material ?? 0;
      anyHit = true;
    }
  }

  return { fraction: bestFrac, normal: { nx: bestNx, ny: bestNy, nz: bestNz }, hit: anyHit, material: bestMat };
}

// --- StayOnGround (Source-style ground snapping) ---
// After every grounded move, trace downward to snap the player to the surface below.
// This is what makes walking on curved surfaces (arches, curved ramps) work —
// horizontal movement is unimpeded, then the player is pulled down to the next segment.

const STAY_UP = FP.from(0.05);   // trace up slightly to escape any surface overlap

export function stayOnGround(
  px: Fixed, py: Fixed, pz: Fixed,
  hw: Fixed, hh: Fixed, hd: Fixed,
  brushes: Brush[],
  stepSize: Fixed,
): { py: Fixed; onGround: boolean; material: number } {
  // Trace up slightly to get out of any surface
  const upTarget = FP.add(py, STAY_UP);
  const upTrace = sweepAllBrushes(px, py, pz, px, upTarget, pz, hw, hh, hd, brushes);
  const safeY = upTrace.hit
    ? FP.add(py, FP.mul(STAY_UP, upTrace.fraction))
    : upTarget;

  // Trace down by stepSize from safe position
  const downTarget = FP.sub(safeY, stepSize);
  const downTrace = sweepAllBrushes(px, safeY, pz, px, downTarget, pz, hw, hh, hd, brushes);

  if (downTrace.hit && downTrace.fraction < FP.ONE && downTrace.normal.ny >= FP.from(0.7)) {
    const newY = FP.add(safeY, FP.mul(FP.sub(downTarget, safeY), downTrace.fraction));
    return { py: newY, onGround: true, material: downTrace.material };
  }

  return { py, onGround: false, material: 0 };
}

// --- Surf ramp helper ---

export function surfRamp(
  x: number, z: number,
  length: number, height: number,
  angle: number, // radians, ramp surface angle from vertical
  direction: number, // radians, which way the ramp faces on the xz plane
  side: "left" | "right"
): Brush {
  // A surf ramp is a wedge: floor, ceiling, back wall, and one angled face
  // The angled face normal determines the surf direction
  const sinA = Math.sin(angle);
  const cosA = Math.cos(angle);
  const sinD = Math.sin(direction);
  const cosD = Math.cos(direction);

  // Angled face normal (points away from the ramp surface)
  const sign = side === "left" ? 1 : -1;
  const fnx = sign * cosA * cosD;
  const fny = sinA;
  const fnz = sign * cosA * sinD;

  const halfLen = length / 2;
  const halfH = height / 2;

  const planes: Plane[] = [
    // Bottom
    { nx: FP.ZERO, ny: -FP.ONE as Fixed, nz: FP.ZERO, d: FP.from(0) },
    // Top
    { nx: FP.ZERO, ny: FP.ONE, nz: FP.ZERO, d: FP.from(height) },
    // Angled surf face
    { nx: FP.from(fnx), ny: FP.from(fny), nz: FP.from(fnz), d: FP.from(fnx * x + fny * halfH + fnz * z) },
    // Back face (opposite of surf face)
    { nx: FP.from(-fnx), ny: FP.from(-fny), nz: FP.from(-fnz), d: FP.from(-(-fnx * x + -fny * halfH + -fnz * z) + 2) },
    // Length caps (along ramp direction)
    { nx: FP.from(-sinD), ny: FP.ZERO, nz: FP.from(cosD), d: FP.from(-sinD * x + cosD * z + halfLen) },
    { nx: FP.from(sinD), ny: FP.ZERO, nz: FP.from(-cosD), d: FP.from(sinD * x - cosD * z + halfLen) },
  ];

  return brush(planes);
}
